import { getSourceCategory, type Source } from '../../services/source.service';

/**
 * How the home page reads a data source's state. Kept apart from the component
 * so the rules can be tested without pulling the UI in.
 */

/**
 * A source that hasn't reported a successful sync in this long is called out as
 * stale — the data is still visible to Ellie, but it may be behind.
 */
export const STALE_AFTER_MS = 3 * 24 * 60 * 60 * 1000;

export type StatusVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning';

export type SourceStatus = {
  label: string;
  variant: StatusVariant;
  /** True when the source isn't usable yet and the user needs to finish setting it up. */
  needsConfiguration: boolean;
  /**
   * A managed integration that hasn't been connected. Only these count toward
   * the "N sources aren't connected" note — a flat file or Snowflake table has
   * no connection to make, it's just waiting on an upload or a table to be
   * picked, so counting them would overstate what's actually wired up wrong.
   */
  isUnconnectedIntegration: boolean;
};

export function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

/**
 * The most recent moment we know data landed for this source, if ever.
 *
 * Only managed integrations report this: `sync_info.last_sync_completed_at` and
 * `last_loaded` are both written by the connector executor, so flat file and
 * Snowflake sources have neither even when they're full of data. Absence here
 * means "we don't know when", never "there's nothing".
 */
export function getLastSyncedAt(source: Source): Date | null {
  const raw = source.sync_info?.last_sync_completed_at || source.last_loaded;
  if (!raw) return null;
  const date = new Date(raw);
  return isNaN(date.getTime()) ? null : date;
}

export function getSourceStatus(
  source: Source,
  lastSyncedAt: Date | null,
  hasData: boolean,
): SourceStatus {
  const category = getSourceCategory(source.type);

  const settled = { needsConfiguration: false, isUnconnectedIntegration: false };

  if (source.fivetran?.connector?.status?.setup_state === 'incomplete') {
    return {
      label: 'Needs setup',
      variant: 'outline',
      needsConfiguration: true,
      isUnconnectedIntegration: true,
    };
  }
  if (source.sync_info?.status === 'SYNCING') {
    return { label: 'Syncing', variant: 'default', ...settled };
  }
  if (source.sync_info?.status === 'QUEUED') {
    return { label: 'Queued', variant: 'outline', ...settled };
  }

  // Nothing landed and nothing ever ran — this one genuinely needs setting up.
  if (!hasData && !lastSyncedAt) {
    return category === 'managed_integration'
      ? {
          label: 'Not connected yet',
          variant: 'outline',
          needsConfiguration: true,
          isUnconnectedIntegration: true,
        }
      : {
          label: 'No data yet',
          variant: 'outline',
          needsConfiguration: true,
          isUnconnectedIntegration: false,
        };
  }

  // Flat files and Snowflake tables don't sync on a schedule, so there's no
  // paused/stale distinction to draw — data is either there or it isn't.
  if (category !== 'managed_integration') {
    return { label: 'Ready', variant: 'success', ...settled };
  }

  if (source.sync_info?.sync_schedule?.enabled === false) {
    return { label: 'Paused', variant: 'secondary', ...settled };
  }
  if (!lastSyncedAt) {
    // Has data but never reported a sync time — say nothing rather than guess.
    return { label: 'Ready', variant: 'success', ...settled };
  }
  if (Date.now() - lastSyncedAt.getTime() > STALE_AFTER_MS) {
    return { label: 'Stale', variant: 'warning', ...settled };
  }
  return { label: 'Synced', variant: 'success', ...settled };
}
